import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import SEOMetaTags from '../components/SEOMetaTags';
import { trackPageView, trackButtonClick } from '../analytics';

const ProtectPdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [userPassword, setUserPassword] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [printPerm, setPrintPerm] = useState('full');
  const [modifyPerm, setModifyPerm] = useState('all');
  const [extractText, setExtractText] = useState(true);
  const [accessibility, setAccessibility] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackPageView('/protect-pdf', 'Protect PDF Page');
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleProtect = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file');
      return;
    }

    if (!userPassword) {
      setError('Please enter the User Password');
      return;
    }

    if (!ownerPassword) {
      setError('Please enter the Owner Password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      trackButtonClick('protect_pdf', 'protect_pdf');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('user_password', userPassword || '');
      formData.append('owner_password', ownerPassword || '');
      
      const permissions = {
        print: printPerm,
        modify: modifyPerm,
        extract: extractText,
        accessibility: accessibility
      };
      formData.append('permissions', JSON.stringify(permissions));

      const response = await fetch('https://api.temp2doc.com/organise/protect', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error: ${response.status} ${response.statusText}`);
      }

      // Get the protected PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `protected_${selectedFile.name}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      trackButtonClick('protect_pdf', 'protect_success');
      setSelectedFile(null);
      setUserPassword('');
      setOwnerPassword('');
      
    } catch (err) {
      console.error('Protection error:', err);
      setError(err.message || 'Failed to protect PDF. Please try again.');
      trackButtonClick('protect_pdf', 'protect_error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMetaTags 
        title="Protect PDF - Encrypt PDF Files with Password Online Free | Temp2Doc"
        description="Protect and encrypt PDF files online for free with 256-bit AES encryption. Add user passwords, owner passwords, and set granular permissions for printing, editing, and copying. Secure your PDFs instantly with professional encryption."
        keywords="protect PDF, encrypt PDF, password protect PDF, PDF security, lock PDF, PDF encryption, secure PDF, PDF password, add password to PDF, PDF permissions, restrict PDF editing, PDF access control"
        canonicalUrl="https://temp2doc.com/protect-pdf"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Protect PDF",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Protect and encrypt PDF files with 256-bit AES encryption. Add passwords and set permissions for printing, editing, and content extraction. Secure your sensitive documents instantly.",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "1240"
          },
          "featureList": [
            "256-bit AES encryption",
            "User password protection",
            "Owner password for permissions",
            "Granular permission controls",
            "Print restrictions (full/low/none)",
            "Modify restrictions (all/annotate/form/assembly/none)",
            "Extract text control",
            "Accessibility settings",
            "No file size limits up to 10MB",
            "Secure processing with automatic file deletion",
            "No registration required",
            "Free online tool"
          ]
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Protect PDF - Encrypt & Secure PDF Files
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Protect your PDFs with 256-bit AES encryption. Add passwords and set granular permissions for printing, editing, and content extraction.
          </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File (Max 10MB)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                      <span>Upload a file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".pdf" onChange={handleFileChange} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 10MB</p>
                </div>
              </div>
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Password Settings */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Password Settings</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Password (to open PDF) <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  placeholder="Enter user password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required password to open and view the PDF file.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Password (to change permissions) <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  placeholder="Enter owner password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required to set and enforce permission controls (printing, editing, copying, etc.).
                </p>
              </div>
            </div>

            {/* Permission Settings */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Permission Settings</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Printing
                </label>
                <select
                  value={printPerm}
                  onChange={(e) => setPrintPerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="full">Full resolution printing</option>
                  <option value="low">Low resolution printing only</option>
                  <option value="none">No printing allowed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modifications
                </label>
                <select
                  value={modifyPerm}
                  onChange={(e) => setModifyPerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All modifications allowed</option>
                  <option value="annotate">Annotations and form fields only</option>
                  <option value="form">Form field fill-in only</option>
                  <option value="assembly">Page assembly only</option>
                  <option value="none">No modifications allowed</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="extract"
                  checked={extractText}
                  onChange={(e) => setExtractText(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="extract" className="ml-2 block text-sm text-gray-700">
                  Allow text extraction and copying
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="accessibility"
                  checked={accessibility}
                  onChange={(e) => setAccessibility(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="accessibility" className="ml-2 block text-sm text-gray-700">
                  Enable accessibility features (screen readers)
                </label>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              onClick={handleProtect}
              disabled={!selectedFile || !userPassword || !ownerPassword || isLoading}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Protecting PDF...' : 'Protect PDF'}
            </button>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Your PDF</h3>
              <p className="text-gray-600 text-sm">
                Select the PDF file you want to protect. Files up to 10MB are supported.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Passwords & Permissions</h3>
              <p className="text-gray-600 text-sm">
                Enter both user and owner passwords (required). Set granular permissions for printing, editing, and copying.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Protected PDF</h3>
              <p className="text-gray-600 text-sm">
                Get your encrypted PDF with 256-bit AES encryption and custom permissions applied.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">256-bit AES Encryption</h3>
              <p className="text-gray-600 text-sm">
                Military-grade encryption automatically applied for maximum security.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Dual Password System</h3>
              <p className="text-gray-600 text-sm">
                Both user and owner passwords required for maximum security and permission control.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Granular Permissions</h3>
              <p className="text-gray-600 text-sm">
                Fine-grained control over printing, editing, copying, and accessibility.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <li>
              <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Chat PDF With AI</span>
              </a>
            </li>
            <li>
              <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Merge PDF</span>
              </a>
            </li>
            <li>
              <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Word</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Excel</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Image</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
              </a>
            </li>
            <li>
              <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Unlock PDF</span>
              </a>
            </li>
            <li>
              <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
              </a>
            </li>
            <li>
              <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Compress PDF</span>
              </a>
            </li>
            <li>
              <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
              </a>
            </li>
            <li>
              <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Back to Home</span>
              </a>
            </li>
          </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default ProtectPdf;
